import React, { useState, useEffect } from 'react';
import { useI18n } from '../hooks/useI18n';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile } from '../hooks/useUserProfile';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { logAnalyticsEvent } from '../hooks/usePwaAnalytics';
import { EditContactModal } from '../components/profile/EditContactModal';
import { EditAddressModal } from '../components/profile/EditAddressModal';
import { EditPersonalModal } from '../components/profile/EditPersonalModal';
import { PwaInstallBanner } from '../components/profile/PwaInstallBanner';
import { toast } from '../components/ui/Toast';
import { sendResetPasswordLink } from '../api/authApi';

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
    <path d="M12 20h9"></path>
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
  </svg>
);

const LogoutIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
    <polyline points="16 17 21 12 16 7"></polyline>
    <line x1="21" y1="12" x2="9" y2="12"></line>
  </svg>
);

const InfoRow = ({ label, value }: { label: string, value: React.ReactNode }) => (
  <div className="info-row">
    <div className="info-label">{label}</div>
    <div className="info-value">{value || '-'}</div>
  </div>
);

export const ProfilePage: React.FC = () => {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const { update } = useUserProfile();
  const { isSupported, isSubscribed, loading: pushLoading, subscribe, unsubscribe } = usePushNotifications();

  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isAddressOpen, setIsAddressOpen] = useState(false);
  const [isPersonalOpen, setIsPersonalOpen] = useState(false);
  
  const [emailLinkSent, setEmailLinkSent] = useState(false);
  const [sendingEmailLink, setSendingEmailLink] = useState(false);
  const [waitingForEmailToSendLink, setWaitingForEmailToSendLink] = useState(false);

  const [isPWA, setIsPWA] = useState(false);
  const [isPwaExpanded, setIsPwaExpanded] = useState(false);

  useEffect(() => {
    const checkPWA = () => {
      const match = window.matchMedia('(display-mode: standalone)').matches;
      const navStandalone = ('standalone' in navigator) && (navigator as any).standalone;
      setIsPWA(match || navStandalone);
    };
    checkPWA();
    window.matchMedia('(display-mode: standalone)').addEventListener('change', checkPWA);
    return () => {
      window.matchMedia('(display-mode: standalone)').removeEventListener('change', checkPWA);
    };
  }, []);

  if (!user) return null;

  const handleSendEmailLink = async () => {
    setSendingEmailLink(true);
    try {
      await sendResetPasswordLink();
      setEmailLinkSent(true);
      toast(t('verify_email_sent') || 'Link został wysłany!');
    } catch {
      toast(t('err_email_send') || 'Wystąpił błąd podczas wysyłania linku.');
    } finally {
      setSendingEmailLink(false);
    }
  };

  const handleContactSave = async (data: any) => {
    const success = await update(data);
    if (success) {
      toast(t('toast_saved'));
      if (waitingForEmailToSendLink && data.email && !data.email.endsWith('.tmp')) {
        setWaitingForEmailToSendLink(false);
        handleSendEmailLink();
      }
    } else {
      toast(t('error_save'));
    }
    return success;
  };

  const handleSave = async (data: any) => {
    const success = await update(data);
    if (success) {
      toast(t('toast_saved'));
    } else {
      toast(t('error_save'));
    }
    return success;
  };

  return (
    <div >

      {/* M-1: Банер для phone-only акаунтів — пропонуємо прив'язати реальний email або підтвердити існуючий */}
      {user.phoneOnly && (
        <div style={{
          background: '#E6D6FA',
          borderRadius: '16px',
          padding: '16px 18px',
          marginBottom: '16px',
          color: '#361777',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}>
          {user.email && !user.email.endsWith('@tabletka24.tmp') ? (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '4px' }}>
                    {t('verify_email_title_existing')}
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.88, lineHeight: 1.4 }}>
                    {t('verify_email_sub_existing')}
                  </div>
                </div>
              </div>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#361777',
                  fontWeight: 600,
                  fontSize: '14px',
                  textDecoration: 'underline',
                  padding: '0',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                }}
                onClick={handleSendEmailLink}
                disabled={sendingEmailLink}
              >
                {sendingEmailLink ? t('sending') : t('verify_email_cta')}
              </button>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '4px' }}>
                    {t('add_email_title')}
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.88, lineHeight: 1.4 }}>
                    {t('add_email_sub')}
                  </div>
                </div>
              </div>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#361777',
                  fontWeight: 600,
                  fontSize: '14px',
                  textDecoration: 'underline',
                  padding: '0',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                }}
                onClick={() => {
                  setWaitingForEmailToSendLink(true);
                  setIsContactOpen(true);
                }}
              >
                {t('add_email_btn')}
              </button>
            </>
          )}
        </div>
      )}

      <PwaInstallBanner 
        className="hide-on-desktop" 
        variant="mobile-inline" 
        onExpandChange={setIsPwaExpanded} 
      />

      <div className="profile-card" style={{ display: isPwaExpanded ? 'none' : 'block' }}>
        <h1 className="profile-main-title">{t('my_account')}</h1>

        <div className="profile-desktop-split">
          <div className="profile-col-left">
            <div className="profile-section">
              <div className="profile-sec-hdr">
                <h3 className="profile-sec-title">{t('dane_personal')}</h3>
                {/* <button className="btn-edit" onClick={() => setIsPersonalOpen(true)}>
                  <EditIcon /> {t('edit')}
                </button> */}
              </div>
              <InfoRow label={t('firstname')} value={user.firstName} />
              <InfoRow label={t('lastname')} value={user.lastName} />
              {user.passport && !user.pesel ? (
                <>
                  <InfoRow label={t('passport_no')} value={user.passport || '-'} />
                  <InfoRow label={t('issue_country')} value={t('poland')} />
                  <InfoRow label={t('issue_date')} value="23.11.2008" />
                </>
              ) : (
                <InfoRow label={t('pesel')} value={user.pesel || '-'} />
              )}
              <InfoRow label={t('sex')} value={user.sex === 'female' ? t('female') : (user.sex === 'male' ? t('male') : '-')} />
            </div>
          </div>
          
          <div className="profile-divider-vert"></div>
          
          <div className="profile-col-right">
            <div className="profile-divider-mob"></div>
            
            <div className="profile-section">
              <div className="profile-sec-hdr">
                <h3 className="profile-sec-title">{t('dane_addr')}</h3>
                <button className="btn-edit" onClick={() => setIsAddressOpen(true)}>
                  <EditIcon /> {t('edit')}
                </button>
              </div>
              <InfoRow label={t('street')} value={user.address?.street || '-'} />
              <InfoRow label={t('building')} value={user.address?.building || user.address?.house || '-'} />
              <InfoRow label={t('zipcode')} value={user.address?.zip || '-'} />
              <InfoRow label={t('city')} value={user.address?.city || '-'} />
            </div>

            <div className="profile-divider-mob"></div>

            <div className="profile-section mt-contact">
              <div className="profile-sec-hdr">
                <h3 className="profile-sec-title">{t('dane_contact')}</h3>
                <button className="btn-edit" onClick={() => setIsContactOpen(true)}>
                  <EditIcon /> {t('edit')}
                </button>
              </div>
              <InfoRow label={t('email')} value={user.email?.endsWith('@tabletka24.tmp') ? '-' : (user.email || '-')} />
              <InfoRow label={t('phone_short')} value={user.phone || '-'} />
            </div>
          </div>
        </div>

        <div className="profile-divider-horiz"></div>

        <div className="profile-bottom">
          <div className="profile-section">
            <h3 className="profile-sec-title" style={{ marginBottom: '20px' }}>{t('security')}</h3>
            
            {!user.phoneOnly && (
              <div className="pwd-row">
                <div className="info-label">{t('password')}</div>
                <div className="pwd-val-row">
                  <span className="pwd-dots">••••••••</span>
                  <button className="btn-link-bold" onClick={handleSendEmailLink} disabled={sendingEmailLink}>
                    {sendingEmailLink ? '...' : t('change')}
                  </button>
                </div>
              </div>
            )}

            {isSupported && isPWA && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                <div className="info-label" style={{ width: 'auto' }}>{t('push_notif')}</div>
                <button 
                  className="btn-link-bold" 
                  disabled={pushLoading}
                  onClick={async () => {
                    if (isSubscribed) {
                      const success = await unsubscribe();
                      if (success) toast(t('push_off_toast'));
                    } else {
                      logAnalyticsEvent('push_permission_requested');
                      const success = await subscribe();
                      if (success) {
                        logAnalyticsEvent('push_permission_granted');
                        toast(t('push_on_toast'));
                      } else {
                        logAnalyticsEvent('push_permission_denied');
                        toast(t('push_err_toast'));
                      }
                    }
                  }}
                >
                  {pushLoading ? '...' : (isSubscribed ? t('turn_off') : t('turn_on'))}
                </button>
              </div>
            )}

            <div className="links-section">
              <a href="https://tabletka24.pl/regulamin/" className="footer-link" dangerouslySetInnerHTML={{ __html: t('terms_link') }}></a>
              <a href="https://tabletka24.pl/polityka-prywatnosci/" className="footer-link" dangerouslySetInnerHTML={{ __html: t('privacy_link') }}></a>
            </div>

            <div className="mobile-logout-row">
              <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start' }}>
                <button 
                  className="btn-link-bold btn-logout" 
                  onClick={() => logout()}
                >
                  <LogoutIcon />
                  <u>{t('logout')}</u>
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Modals */}
      <EditContactModal 
        isOpen={isContactOpen} 
        onClose={() => {
          setIsContactOpen(false);
          setWaitingForEmailToSendLink(false);
        }} 
        user={user} 
        onSave={handleContactSave} 
      />
      
      <EditAddressModal 
        isOpen={isAddressOpen} 
        onClose={() => setIsAddressOpen(false)} 
        user={user} 
        onSave={handleSave} 
      />

      <EditPersonalModal 
        isOpen={isPersonalOpen} 
        onClose={() => setIsPersonalOpen(false)} 
        user={user} 
        onSave={handleSave} 
      />

      <style>{`
        .profile-card {
        background: #FAF8FE;
          border-radius: 24px;
          width: 100%;
        }
        .profile-main-title {
          font-size: 24px;
          font-weight: 700;
          color: var(--cab-violet);
          margin: 0 0 32px 0;
        }
        .profile-desktop-split {
          display: flex;
          align-items: stretch;
          margin-top: 24px;  
        }
        .profile-col-left {
          flex: 1;
          padding-right: 32px;
        }
        .profile-col-right {
          flex: 1;
          padding-left: 32px;
        }
        .profile-divider-vert {
          width: 1px;
          background: #E6D6FA;
          margin: 0;
        }
        .profile-divider-mob {
          display: none;
          height: 1px;
          background: #E6D6FA;
          margin: 24px 0;
        }
        .profile-divider-horiz {
          height: 1px;
          background: #E6D6FA;
          margin: 32px 0;
        }
        .profile-section {
          display: flex;
          flex-direction: column;
        }
        .mt-contact {
          margin-top: 32px;
        }
        .profile-sec-hdr {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }
        .profile-sec-title {
          font-size: 18px;
          font-weight: 700;
          color: var(--cab-violet);
          margin: 0;
        }
        .btn-edit {
          display: flex;
          align-items: center;
          background: none;
          border: none;
          color: var(--cab-violet);
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          padding: 0;
          font-family: inherit;
        }
        .info-row {
          display: flex;
          margin-bottom: 12px;
          font-size: 15px;
          line-height: 1.4;
          align-items: flex-start;
        }
        .info-row:last-child {
          margin-bottom: 0;
        }
        .info-label {
          width: 140px;
          flex-shrink: 0;
          color: var(--cab-note);
          font-weight: 400;
        }
        .info-value {
          color: var(--cab-violet);
          font-weight: 500;
          flex-grow: 1;
          word-break: break-word;
        }
        .pwd-row {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
        }
        .pwd-val-row {
          display: flex;
          align-items: center;
          flex-grow: 1;
        }
        .pwd-dots {
          letter-spacing: 4px;
          font-size: 24px;
          color: var(--cab-violet);
          margin-right: 24px;
          line-height: 1;
          transform: translateY(4px);
        }
        .btn-link-bold {
          background: none;
          border: none;
          color: var(--cab-violet);
          font-size: 14px;
          font-weight: 600;
          text-decoration: underline;
          cursor: pointer;
          padding: 0;
          font-family: inherit;
        }
        .links-section {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-top: 32px;
        }
        .mobile-logout-row {
          display: none;
        }
        .btn-logout {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #D92D20;
        }
        .footer-link {
          color: var(--cab-violet);
          font-size: 14px;
          text-decoration: none;
        }
        .footer-link u {
          font-weight: 600;
          text-decoration: underline;
        }

        @media (max-width: 1023px) {
          .profile-card {
            padding: 24px 20px;
            background: #FAF8FE;
            border-radius: 20px;
          }
          .profile-main-title {
            margin-bottom: 24px;
          }
          .profile-desktop-split {
            flex-direction: column;
          }
          .profile-col-left {
            padding-right: 0;
          }
          .profile-col-right {
            padding-left: 0;
          }
          .profile-divider-vert {
            display: none;
          }
          .profile-divider-mob {
            display: block;
          }
          .profile-divider-horiz {
            margin: 24px 0;
          }
          .mobile-logout-row {
            display: flex;
            align-items: center;
            margin-top: 40px;
          }
          .mt-contact {
            margin-top: 0;
          }
          .info-label {
            width: 130px;
          }
        }
      `}</style>
    </div>
  );
};

